import React, { useEffect, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { getDentalActivityTimeline } from '../../../services/api/cashless-dentals';
import { getBlobUrl } from '../../../utils/getBlobUrlAndContentType';
import LoadingComponent from '../../common/LoadingComponent';

// React error boundary so a render-time crash in any descendant
// doesn't tear down the parent modal / surrounding page.
class TimelineErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[AppointmentTimeline][ErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            color: '#a05a00',
            fontSize: '0.9rem',
            padding: '0.75rem',
            background: '#fff7e6',
            border: '1px solid #f0c97a',
            borderRadius: '6px',
          }}
        >
          Could not render the activity timeline. The rest of the page is still usable.
        </div>
      );
    }
    return this.props.children;
  }
}

const getActorLabel = (entry) => {
  try {
    if (!entry || !entry.actorName) return null;
    if (entry.source === 'network_center_activities') return `Clinic: ${entry.actorName}`;
    if (entry.userType === 'user') return `User: ${entry.actorName}`;
    if (entry.userType === 'doctor') return `Doctor: ${entry.actorName}`;
    if (entry.userType === 'ops') return `Ops: ${entry.actorName}`;
    return `Agent: ${entry.actorName}`;
  } catch (e) {
    return null;
  }
};

const formatDate = (isoString) => {
  try {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch (e) {
    return '';
  }
};

// "totalWalletDeduction" -> "Total Wallet Deduction"
const formatMetaKey = (key) => {
  try {
    if (typeof key !== 'string') return String(key);
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  } catch (e) {
    return String(key);
  }
};

const formatMetaValue = (value) => {
  try {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  } catch (e) {
    return '—';
  }
};

const MetaRow = ({ label, children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px' }}>
    <span style={{ color: '#555', fontWeight: 500 }}>{label}:</span>
    <span style={{ color: '#222' }}>{children}</span>
  </div>
);

const renderMeta = (meta, attachments) => {
  let entries;
  try {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null;

    // Skip keys whose value is a URL already rendered as an attachment.
    const attachmentUrls = new Set(
      Array.isArray(attachments)
        ? attachments.map((a) => (a && typeof a.url === 'string' ? a.url : null)).filter(Boolean)
        : []
    );

    entries = Object.entries(meta).filter(
      ([, value]) => !(typeof value === 'string' && attachmentUrls.has(value))
    );

    if (entries.length === 0) return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[AppointmentTimeline][renderMeta][prepare]', e);
    return null;
  }

  return (
    <div
      style={{
        marginTop: '6px',
        background: '#f7f5fc',
        borderRadius: '6px',
        padding: '8px 10px',
        fontSize: '0.8rem',
      }}
    >
      {entries.map(([key, value]) => {
        try {
          // Nested array of objects (e.g. meta.procedures = [{...}, {...}])
          if (Array.isArray(value)) {
            if (value.length === 0) {
              return <MetaRow key={key} label={formatMetaKey(key)}>—</MetaRow>;
            }
            return (
              <div key={key} style={{ marginTop: '4px' }}>
                <div style={{ color: '#555', fontWeight: 500 }}>{formatMetaKey(key)}:</div>
                <div style={{ paddingLeft: '8px', marginTop: '2px' }}>
                  {value.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginTop: idx > 0 ? '6px' : '2px',
                        borderLeft: '2px solid #c9bfed',
                        paddingLeft: '8px',
                      }}
                    >
                      {item && typeof item === 'object'
                        ? Object.entries(item).map(([k, v]) => (
                            <div key={k} style={{ marginTop: '2px' }}>
                              <span style={{ color: '#777' }}>{formatMetaKey(k)}:</span>{' '}
                              <span style={{ color: '#222' }}>{formatMetaValue(v)}</span>
                            </div>
                          ))
                        : (
                          <span style={{ color: '#222' }}>{formatMetaValue(item)}</span>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Nested object — render as inline JSON so nothing gets hidden.
          if (value && typeof value === 'object') {
            let serialised;
            try {
              serialised = JSON.stringify(value);
            } catch (e) {
              serialised = '[unrenderable]';
            }
            return (
              <MetaRow key={key} label={formatMetaKey(key)}>
                <code style={{ background: '#fff', padding: '0 4px', borderRadius: '3px' }}>
                  {serialised}
                </code>
              </MetaRow>
            );
          }

          // Primitive
          return (
            <MetaRow key={key} label={formatMetaKey(key)}>
              {formatMetaValue(value)}
            </MetaRow>
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[AppointmentTimeline][renderMeta][row]', { key, value, err: e });
          return (
            <MetaRow key={key} label={formatMetaKey(key)}>
              <span style={{ color: '#a05a00' }}>[unrenderable]</span>
            </MetaRow>
          );
        }
      })}
    </div>
  );
};

const AppointmentTimeline = ({ requestId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    setLoading(true);
    getDentalActivityTimeline(requestId)
      .then((res) => {
        setHistory(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        console.error('[AppointmentTimeline]', err);
        setHistory([]);
      })
      .finally(() => setLoading(false));
  }, [requestId]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (history.length === 0) {
    return (
      <div style={{ color: '#999', fontSize: '1rem', padding: '1rem 0' }}>
        No timeline events found.
      </div>
    );
  }

  const safeHistory = Array.isArray(history) ? history : [];

  return (
    <TimelineErrorBoundary>
    <div>
      <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#4b4f5b', marginBottom: '0.75rem' }}>
        Timeline
      </div>
      <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
        {safeHistory.map((entry, index) => {
          if (!entry || typeof entry !== 'object') return null;
          try {
          const actorLabel = getActorLabel(entry);
          const isLast = index === safeHistory.length - 1;
          return (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', marginBottom: '1.25rem', position: 'relative' }}>
              {/* Circle dot */}
              <div style={{
                position: 'absolute',
                left: '-1.5rem',
                top: '4px',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid #6C5DD3',
                backgroundColor: '#fff',
                flexShrink: 0,
                zIndex: 1,
              }} />
              {/* Vertical connector line */}
              {!isLast && (
                <div style={{
                  position: 'absolute',
                  left: '-1.5rem',
                  top: '18px',
                  marginLeft: '6px',
                  width: '2px',
                  bottom: '-1.25rem',
                  backgroundColor: '#d0cce8',
                }} />
              )}
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#222' }}>
                  {entry.description || 'Activity'}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#666', marginTop: '2px' }}>
                  {formatDate(entry.createdAt)}
                </div>
                {actorLabel && (
                  <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '2px' }}>
                    {actorLabel}
                  </div>
                )}
                {entry.attachments && entry.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                    {entry.attachments.map((att, attIdx) => (
                      <div
                        key={attIdx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: '#f0edf8',
                          border: '1px solid #c9bfed',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          maxWidth: '130px',
                        }}
                        onClick={async () => {
                          try {
                            const url = await getBlobUrl(att.url);
                            window.open(url, '_blank');
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('[AppointmentTimeline][attachment-click]', err);
                          }
                        }}
                      >
                        <FaEye size={13} style={{ color: '#6C5DD3', flexShrink: 0 }} />
                        <span style={{
                          marginLeft: '5px',
                          fontSize: '0.78rem',
                          color: '#6C5DD3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {att.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {renderMeta(entry.meta, entry.attachments)}
              </div>
            </div>
          );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[AppointmentTimeline][entry-render]', { index, entry, err });
            return (
              <div
                key={index}
                style={{
                  color: '#a05a00',
                  fontSize: '0.85rem',
                  marginBottom: '1.25rem',
                  padding: '6px 10px',
                  background: '#fff7e6',
                  border: '1px solid #f0c97a',
                  borderRadius: '6px',
                }}
              >
                Could not render this event.
              </div>
            );
          }
        })}
      </div>
    </div>
    </TimelineErrorBoundary>
  );
};

export default AppointmentTimeline;
